import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { adminQueries, adminQueryKeys, type LogQuery } from "@jkast90/kast-common/admin-core";
import { adminRequest, adminService } from "./services/adminService";
import { galleryService } from "./services/galleryService";
import { impactService } from "./services/impactService";
import { shopService } from "./services/shopService";

// One key per kind of server data, so a write names what it changed.
export const queryKeys = {
  shopStatus: (preview: boolean) => ["shop", "status", preview] as const,
  shopStatuses: ["shop", "status"] as const,
  shopItems: (preview: boolean) => ["shop", "items", preview] as const,
  shopItem: (id: string, preview: boolean) => ["shop", "item", id, preview] as const,
  order: (id: string, token: string) => ["shop", "order", id, token] as const,
  stats: ["impact", "stats"] as const,
  kennelStats: ["impact", "kennel-stats"] as const,
  kennels: ["impact", "kennels"] as const,
  locations: ["impact", "locations"] as const,
  linkableSubmissions: ["impact", "linkable-submissions"] as const,
  gallery: ["gallery", "approved"] as const,
  admin: {
    stats: ["admin", "stats"] as const,
    summary: ["admin", "summary"] as const,
    submissions: ["admin", "submissions"] as const,
    locations: ["admin", "locations"] as const,
    photos: ["admin", "photos"] as const,
    shopItems: ["admin", "shop-items"] as const,
    stockOwners: ["admin", "stock-owners"] as const,
    orders: ["admin", "orders"] as const,
    settings: ["admin", "settings"] as const,
    // The shared admin endpoints' keys are the package's (`adminQueryKeys`), below.
  },
};

// ---- the storefront and the public site

export const useShopStatusQuery = (preview: boolean, enabled = true) =>
  useQuery({ queryKey: queryKeys.shopStatus(preview), queryFn: () => shopService.getStatus(), enabled });

export const useShopItems = (preview: boolean, enabled: boolean) =>
  useQuery({ queryKey: queryKeys.shopItems(preview), queryFn: () => shopService.listItems(), enabled });

export const useShopItem = (id: string | undefined, preview: boolean, enabled: boolean) =>
  useQuery({
    queryKey: queryKeys.shopItem(id ?? "", preview),
    queryFn: () => shopService.getItem(id ?? ""),
    enabled: enabled && !!id,
  });

export const useOrder = (id: string | undefined, token: string) =>
  useQuery({
    queryKey: queryKeys.order(id ?? "", token),
    queryFn: () => shopService.getOrder(id ?? "", token),
    enabled: !!id,
  });

export const useImpactStats = () => useQuery({ queryKey: queryKeys.stats, queryFn: () => impactService.getStats() });

export const useKennelStats = () =>
  useQuery({ queryKey: queryKeys.kennelStats, queryFn: () => impactService.getKennelStats() });

export const useKennels = () => useQuery({ queryKey: queryKeys.kennels, queryFn: () => impactService.getKennels() });

export const useLocations = () =>
  useQuery({ queryKey: queryKeys.locations, queryFn: () => impactService.getLocations() });

export const useLinkableSubmissions = () =>
  useQuery({ queryKey: queryKeys.linkableSubmissions, queryFn: () => impactService.getLinkableSubmissions() });

export const useApprovedPhotos = () =>
  useQuery({ queryKey: queryKeys.gallery, queryFn: () => galleryService.listApproved(100) });

// ---- the admin panel

export const useAdminStats = () =>
  useQuery({ queryKey: queryKeys.admin.stats, queryFn: () => adminService.getStats() });

export const useAdminSummary = () =>
  useQuery({ queryKey: queryKeys.admin.summary, queryFn: () => adminService.getSummary() });

export const useAdminSubmissions = () =>
  useQuery({ queryKey: queryKeys.admin.submissions, queryFn: () => adminService.listSubmissions("all") });

export const useAdminLocations = () =>
  useQuery({ queryKey: queryKeys.admin.locations, queryFn: () => adminService.listLocations() });

/** Each status is its own newest-first list (the API has no cursor), `limit` of them. */
export const useAdminPhotos = (status: string, limit: number) =>
  useQuery({
    queryKey: [...queryKeys.admin.photos, status, limit],
    queryFn: () => galleryService.listAll(status, limit),
    placeholderData: keepPreviousData,
  });

export const useAdminShopItems = () =>
  useQuery({ queryKey: queryKeys.admin.shopItems, queryFn: () => adminService.listShopItems() });

export const useStockOwners = () =>
  useQuery({ queryKey: queryKeys.admin.stockOwners, queryFn: () => adminService.listStockOwners() });

export const useAdminOrders = () =>
  useQuery({ queryKey: queryKeys.admin.orders, queryFn: () => adminService.listOrders("all") });

export const useAdminSettings = () =>
  useQuery({ queryKey: queryKeys.admin.settings, queryFn: () => adminService.getSettings() });

// The shared admin views, keyed as every app keys them (the package's
// adminQueryKeys), read through the app's client (adminService's fetchers,
// which the tabs' tests stub).
export const useAdminVisitors = () =>
  useQuery({ queryKey: adminQueryKeys.visitors(), queryFn: () => adminService.listVisitors() });

export const useAdminDeploys = () =>
  useQuery({ queryKey: adminQueryKeys.deploys(), queryFn: () => adminService.listDeploys() });

export const useAdminLambdas = () =>
  useQuery({ queryKey: adminQueryKeys.lambdas(), queryFn: () => adminService.listLambdas() });

/** The Logs and Members tabs: the package's queries over the app's client (GET /admin/logs, /admin/members). */
export const adminQuery = adminQueries(adminRequest);

export const useAdminLogs = (query: LogQuery) => useQuery(adminQuery.logs(query));

export const useAdminMembers = () => useQuery(adminQuery.members);

/**
 * After a write: reloads the lists it touched, and the counts the header's
 * stats strip shows (a tab switch alone doesn't refetch those).
 */
export function useAdminChanged() {
  const client = useQueryClient();
  return (...keys: (readonly unknown[])[]) =>
    Promise.all(
      [...keys, queryKeys.admin.stats, queryKeys.admin.summary].map((queryKey) =>
        client.invalidateQueries({ queryKey }),
      ),
    );
}
